import { useEffect, useMemo, useState } from "react";
import { api, getDepartmentFilter } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";

export default function Sedes() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", department_id: "" });
  const [deps, setDeps] = useState([]);

  const load = () => api.get("/sedes").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/departments").then((r) => setDeps(r.data || [])).catch(() => {}); }, []);
  const depName = (id) => deps.find((d) => d.id === (id || "dep_es"))?.short_name || "";

  // Alphabetical directory: sort sedes A→Z (Spanish locale, accent-insensitive).
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [items],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", address: "", department_id: getDepartmentFilter() || "dep_es" });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, address: s.address || "", department_id: s.department_id || "dep_es" });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) await api.put(`/sedes/${editing.id}`, form);
      else await api.post("/sedes", form);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch { toast.error(t("error_generic")); }
  };

  const remove = async (id, name) => {
    const ok = await confirm({
      title: "¿Borrar sede?",
      description: `Se eliminará permanentemente "${name || 'esta sede'}". Los grupos y estudiantes asociados quedarán sin sede.\n\nEsta acción no se puede deshacer.`,
      confirmText: "Sí, borrar",
      danger: true,
    });
    if (!ok) return;
    await api.delete(`/sedes/${id}`);
    toast.success(t("deleted"));
    load();
  };

  return (
    <div>
      <PageHeader
        title={t("sedes")}
        testId="sedes-header"
        action={
          <Button data-testid="new-sede-button" onClick={openCreate}>
            <Plus className="h-4 w-4" /> {t("new_sede")}
          </Button>
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("address")}</TableHead>
              <TableHead>Departamento</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-10">{t("no_data")}</TableCell></TableRow>
            )}
            {sortedItems.map((s) => (
              <TableRow key={s.id} data-testid={`sede-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-slate-600">{s.address}</TableCell>
                <TableCell className="text-slate-600" data-testid={`sede-dept-${s.id}`}>{depName(s.department_id)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-sede-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s.id, s.name)} className="text-red-600" data-testid={`delete-sede-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_sede")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("name")}</Label>
              <Input data-testid="sede-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>{t("address")}</Label>
              <Input data-testid="sede-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <Label>Departamento</Label>
              <select data-testid="sede-department-select" value={form.department_id}
                      onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                      className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm">
                {deps.map((d) => <option key={d.id} value={d.id}>{d.short_name || d.name}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-sede-button" onClick={submit}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
